import React, { Profiler, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CustomDateSelector from "components/CustomDateSelector";
import HourlyRateHoverCell from "components/HourlyRateHoverCell";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { useWebSocket } from "contexts/WebSocketContext";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Desktop track weights: Notes carries 2.8/5.6 = 50% of the surplus, because
// it is the one column that gets MORE useful with width. The four data columns
// keep their minmax floors (room for "01:52 PM", "1h52m" and the status chip)
// and stay proportional on wide screens, so the Notes cell — the one that
// decides text vs icon — clears its readability threshold at roughly half the
// entries-area width. The header row below reuses this constant, so labels stay
// aligned with the columns.
const TIMESHEET_DESKTOP_COLUMNS =
  "minmax(115px, 0.75fr) minmax(115px, 0.75fr) minmax(95px, 0.6fr) minmax(110px, 0.7fr) minmax(260px, 2.8fr)";
const TIMESHEET_DAY_COLUMN_WIDTH = "260px";
// The desktop table is wider than the entries area at essentially every window
// size (288px sidebar + settings nav + paddings leave ~550–700px even at
// 1440px), so the column-header row and the day-group cards both live inside
// the list scroller's horizontal overflow. They MUST share one minimum width:
// the wider of the two is what the scroller measures, so if the other ends
// earlier, scrolling right reveals empty page background past its border.
//
// Arithmetic (desktop/md, theme spacing = 8, border-box):
//   260  day column (TIMESHEET_DAY_COLUMN_WIDTH)
// + 695  5 desktop track minimums (115 + 115 + 95 + 110 + 260)
// +  32  4 × 8px gaps in the entry grid
// +  24  entry-row px: 1.5 (12px each side)
// +   2  day-card 1px borders
// = 1013  what a day-group card needs for its rows to fit uncut.
// The header row needs 13px more: its own 2px borders + 24px px: 1.5 sit
// outside the grid, and the inner label grid adds borderLeft 1px + pl: 1.5
// (13px) left of the first data column → 260 + 740 + 26 = 1026. Both use the
// larger figure so their right borders land on the same x; the card's extra
// 13px flows into the fr distribution of the tracks and is not visible.
const TIMESHEET_TABLE_MIN_WIDTH = 1026;
const TIMESHEETS_PERF_QUERY_PARAM = "timesheetsPerf";
const TIMESHEETS_PERF_STORAGE_KEY = "ce_portal.timesheetsPerf";
const TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY = "ce_portal.timesheets.detailsPanelExpanded";
const TIMESHEETS_PERF_LOG_THRESHOLD_MS = 8;
const TIMESHEETS_EVENT_LOOP_LAG_THRESHOLD_MS = 200;

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTimeOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  // Display HH:mm only — the underlying Date still keeps seconds for
  // calculations (duration, totals, etc.).
  // (HH:mm display only)
  return parsed.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getDurationLabel = (clockInAt, clockOutAt) => {
  if (!clockInAt || !clockOutAt) return "In progress";

  const start = new Date(clockInAt);
  const end = new Date(clockOutAt);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "-";
  }

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "0m";

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h${minutes}m`;
};

const getLiveDurationLabel = (clockInAt, nowValue) => {
  if (!clockInAt || !nowValue) return "In progress";

  const start = new Date(clockInAt);
  const end = new Date(nowValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "In progress";
  }

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "0m";

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h${minutes}m`;
};

const getSafeLiveEndTime = (clockInAt, nowValue) => {
  const start = clockInAt ? new Date(clockInAt) : null;
  const nowDate = nowValue ? new Date(nowValue) : null;

  if (!start || Number.isNaN(start.getTime())) {
    return nowDate && !Number.isNaN(nowDate.getTime()) ? nowDate : null;
  }

  if (!nowDate || Number.isNaN(nowDate.getTime())) {
    return start;
  }

  return nowDate.getTime() < start.getTime() ? start : nowDate;
};

const formatMinutesAsHHmm = (minutesValue) => {
  const safeMinutes = Number.isFinite(minutesValue) ? Math.max(0, Math.round(minutesValue)) : 0;
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const getWorkdaysInMonth = (year, monthIndexZeroBased) => {
  if (!Number.isInteger(year) || !Number.isInteger(monthIndexZeroBased) || monthIndexZeroBased < 0 || monthIndexZeroBased > 11) {
    return null;
  }

  const daysInMonth = new Date(year, monthIndexZeroBased + 1, 0).getDate();
  let workdays = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    const weekday = new Date(year, monthIndexZeroBased, day).getDay();
    if (weekday !== 0 && weekday !== 6) {
      workdays += 1;
    }
  }

  return workdays;
};

const getTotalDurationLabel = (entries) => {
  // Completed-only total: entries with a clock-out.
  const completedMs = entries.reduce((sum, entry) => {
    if (!entry.clockInAt || !entry.clockOutAt) {
      return sum;
    }

    const start = new Date(entry.clockInAt);
    const end = new Date(entry.clockOutAt);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return sum;
    }

    const diffMs = end.getTime() - start.getTime();
    return diffMs > 0 ? sum + diffMs : sum;
  }, 0);

  // Running total: completed entries + in-progress entries using their live
  // end time (clockInAt → now), so the parenthetical includes the current
  // in-progress time (ISSUES_TRACKER #19).
  const totalMs = entries.reduce((sum, entry) => {
    if (!entry.clockInAt) {
      return sum;
    }

    const endValue = entry.clockOutAt || entry.liveClockOutAt;
    if (!endValue) {
      return sum;
    }

    const start = new Date(entry.clockInAt);
    const end = new Date(endValue);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return sum;
    }

    const diffMs = end.getTime() - start.getTime();
    return diffMs > 0 ? sum + diffMs : sum;
  }, 0);

  const hasInProgress = entries.some((entry) => !entry.clockOutAt);

  const toLabel = (ms) => {
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (totalMinutes === 0) return "0m";
    return hours > 0 ? `${hours}h${minutes}m` : `${minutes}m`;
  };

  // No in-progress entry → plain total.
  if (!hasInProgress) {
    return toLabel(totalMs);
  }

  // With an in-progress entry: completed total, then the running total
  // (including the live in-progress time) in parentheses.
  return `${toLabel(completedMs)} (~${toLabel(totalMs)} With In Progress)`;
};

// ─── Notes cell: dynamic text-vs-icon (2026-09-24) ──────────────────────────
// The threshold means READABLE, not "wider than nothing". At body2 (~14px) a
// genuinely readable line needs ~48 characters, i.e. ~380px; below that a note
// wraps to 2–3 words per line, which is strictly worse than the icon.
//   cell ≥ 380px → full note as text
//   cell < 380px → note icon, full note on hover
// We measure the CELL itself, not the window: the notes track is only the
// grid's leftover after the fixed 288px app sidebar, the settings nav, the
// 260px day column and the row/gap chrome, so only a real measurement knows.
// (The previous 200px threshold was dead code — the notes track's own 260px
// minmax minimum kept the measured cell at ≥260px in every layout, so the
// icon branch could never render.)
const TIMESHEET_NOTE_TEXT_MIN_WIDTH = 380;

// One ResizeObserver per notes cell; the observer is only attached while it is
// needed (desktop + entry has a note) and disconnected on unmount. State only
// updates when the threshold boolean flips, so moving the divider by a pixel
// never re-renders rows.
const useElementWidthAtLeast = (ref, threshold, enabled = true) => {
  const [isWideEnough, setIsWideEnough] = useState(false);

  // useLayoutEffect so the first measure lands before paint — the cell never
  // flashes the icon and then swaps to text on mount.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return undefined;

    const measure = () => {
      const next = node.getBoundingClientRect().width >= threshold;
      setIsWideEnough((prev) => (prev === next ? prev : next));
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold, enabled]);

  return isWideEnough;
};

const TimesheetNotesCell = ({ isMobile, notes, hasNotes }) => {
  const theme = useTheme();
  const cellRef = useRef(null);
  const showNoteText = useElementWidthAtLeast(
    cellRef,
    TIMESHEET_NOTE_TEXT_MIN_WIDTH,
    !isMobile && hasNotes
  );

  return (
    <Box
      ref={cellRef}
      sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}
    >
      {isMobile && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.25 }}
        >
          Notes
        </Typography>
      )}
      {isMobile || !hasNotes || showNoteText ? (
        <Typography
          variant="body2"
          color={hasNotes ? "text.primary" : "text.secondary"}
          sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
        >
          {notes}
        </Typography>
      ) : (
        // Narrow desktop column: a long note here wraps word-by-word. Show the
        // note icon instead and reveal the full note (pre-line keeps its line
        // breaks) on hover. The Tooltip only mounts in this branch.
        <Tooltip
          arrow
          title={
            <Box
              sx={{
                whiteSpace: "pre-line",
                maxWidth: 340,
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              {notes}
            </Box>
          }
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 28,
              minHeight: 28,
            }}
          >
            <StickyNote2RoundedIcon
              fontSize="small"
              sx={{
                color: theme.palette.primary.main,
                cursor: "help",
                display: "block",
              }}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const TimesheetsTab = forwardRef(({ isMobile, hideHeader = false, useOuterGridCard = false, hourlyRate = 0, onDateRangePropsChange = null, memberId = null, memberName = "", allowEdit = true, inlineStatsAndFilter = false }, ref) => {
  const theme = useTheme();
  // At md+ the KPI row renders all 6 cards across (≈120–180px each), so the
  // h3/h4 value variants clip. Treat md+ as compact stats (h6 values, caption
  // labels); xs (swipe strip) and sm (3×2 cards) keep their existing sizing.
  const isCompactStats = useMediaQuery(theme.breakpoints.up("md"));
  const { socket } = useWebSocket();
  const { setOverlayData, setOverlayName, refreshPage } = useStateContext();

  const [timesheets, setTimesheets] = useState([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [monthsByYear, setMonthsByYear] = useState({});
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [detailsPanelExpanded, setDetailsPanelExpanded] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    const rawValue = window.localStorage.getItem(TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY);
    if (rawValue === "false") {
      return false;
    }

    return true;
  });
  const lastDateRangeSignatureRef = useRef("");
  // The shared refresh signal starts `false`; this guard keeps the first mount
  // from being treated as a refresh request (that would double-fetch).
  const lastRefreshPageRef = useRef(refreshPage);
  // Mirrors the selected year/month so fetchDateOptions can preserve the
  // user's view without taking them as dependencies (that would make its own
  // mount effect re-run on every selection change).
  const selectionRef = useRef({ year: null, month: null });

  const normalizedMemberId = useMemo(() => {
    const parsed = Number.parseInt(String(memberId || ""), 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [memberId]);

  const timesheetsBasePath = useMemo(() => {
    return normalizedMemberId
      ? `/timesheets/member/${normalizedMemberId}`
      : "/timesheets/me";
  }, [normalizedMemberId]);

  const perfEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;

    const params = new URLSearchParams(window.location.search);
    if (params.get(TIMESHEETS_PERF_QUERY_PARAM) === "1") {
      return true;
    }

    return window.localStorage.getItem(TIMESHEETS_PERF_STORAGE_KEY) === "1";
  }, []);

  const measurePerf = useCallback((label, compute) => {
    if (!perfEnabled || typeof performance === "undefined") {
      return compute();
    }

    const startedAt = performance.now();
    const result = compute();
    const elapsedMs = performance.now() - startedAt;

    if (elapsedMs >= TIMESHEETS_PERF_LOG_THRESHOLD_MS) {
      console.info(`[TimesheetsPerf] ${label}: ${elapsedMs.toFixed(2)}ms`);
    }

    return result;
  }, [perfEnabled]);

  const onTimesheetsProfilerRender = useCallback((id, phase, actualDuration, baseDuration, startTime, commitTime) => {
    if (!perfEnabled) return;

    if (actualDuration >= TIMESHEETS_PERF_LOG_THRESHOLD_MS) {
      console.info(
        `[TimesheetsPerf][Profiler] ${id} ${phase} actual=${actualDuration.toFixed(2)}ms base=${baseDuration.toFixed(2)}ms start=${startTime.toFixed(2)} commit=${commitTime.toFixed(2)}`
      );
    }
  }, [perfEnabled]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(
      TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY,
      String(detailsPanelExpanded),
    );
  }, [detailsPanelExpanded]);

  useEffect(() => {
    if (!perfEnabled || typeof window === "undefined") return undefined;

    console.info(
      `[TimesheetsPerf] enabled (${TIMESHEETS_PERF_QUERY_PARAM}=1 or localStorage ${TIMESHEETS_PERF_STORAGE_KEY}=1)`
    );

    let longTaskObserver = null;
    if (typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
      longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          console.warn(
            `[TimesheetsPerf][LongTask] ${entry.duration.toFixed(2)}ms at ${entry.startTime.toFixed(2)}ms`
          );
        });
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
    }

    let expectedAt = Date.now() + 1000;
    const lagInterval = window.setInterval(() => {
      const nowTs = Date.now();
      const lagMs = nowTs - expectedAt;
      if (lagMs >= TIMESHEETS_EVENT_LOOP_LAG_THRESHOLD_MS) {
        console.warn(`[TimesheetsPerf][EventLoopLag] ${lagMs}ms`);
      }
      expectedAt = nowTs + 1000;
    }, 1000);

    return () => {
      window.clearInterval(lagInterval);
      if (longTaskObserver) {
        longTaskObserver.disconnect();
      }
    };
  }, [perfEnabled]);

  const fetchDateOptions = useCallback(async () => {
    try {
      const response = await api.get(`${timesheetsBasePath}/date-options`);
      const payload = response?.data || {};

      const yearsWithData = Array.isArray(payload.availableYears)
        ? payload.availableYears.map((year) => String(year))
        : [];

      const monthMap = Object.entries(payload.monthsByYear || {}).reduce(
        (acc, [year, months]) => {
          acc[String(year)] = Array.isArray(months)
            ? months
                .filter((month) => MONTH_NAMES.includes(month))
                .sort(
                  (left, right) =>
                    MONTH_NAMES.indexOf(left) - MONTH_NAMES.indexOf(right),
                )
            : [];
          return acc;
        },
        {},
      );

      setAvailableYears(yearsWithData);
      setMonthsByYear(monthMap);

      if (yearsWithData.length > 0) {
        // Preserve the current selection when it is still valid.
        //
        // This runs on every WebSocket refresh, and it used to unconditionally
        // jump to the newest year/month that has data. Saving a timeslot
        // therefore yanked the list to a different month — which looks like
        // "the timesheets did not refresh" because the edited row was no
        // longer on screen. Only fall back to the newest month when nothing
        // valid is selected (first load, or the selected month lost its data).
        const { year: currentYear, month: currentMonth } = selectionRef.current;
        const yearStillValid =
          currentYear && yearsWithData.includes(currentYear);

        if (yearStillValid) {
          const validMonths = monthMap[currentYear] || [];

          if (currentMonth && validMonths.includes(currentMonth)) {
            return;
          }

          const fallbackMonth = validMonths[validMonths.length - 1];
          setSelectedMonths(fallbackMonth ? [fallbackMonth] : []);
          return;
        }

        const latestYear = yearsWithData[yearsWithData.length - 1];
        const latestYearMonths = monthMap[latestYear] || [];
        const latestMonth = latestYearMonths[latestYearMonths.length - 1];

        setSelectedYears([latestYear]);
        setSelectedMonths(latestMonth ? [latestMonth] : []);
        return;
      }

      setSelectedYears([]);
      setSelectedMonths([]);
    } catch (error) {
      setAvailableYears([]);
      setMonthsByYear({});
      setSelectedYears([]);
      setSelectedMonths([]);
    }
  }, [timesheetsBasePath]);

  const fetchTimesheets = useCallback(async () => {
    const selectedYear = selectedYears[0];
    if (!selectedYear) {
      setTimesheets([]);
      return;
    }

    setLoadingTimesheets(true);
    try {
      const params = { year: selectedYear };
      if (selectedMonths[0]) {
        params.months = selectedMonths[0];
      }

      const response = await api.get(timesheetsBasePath, { params });
      setTimesheets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setTimesheets([]);
    } finally {
      setLoadingTimesheets(false);
    }
  }, [selectedMonths, selectedYears, timesheetsBasePath]);

  useEffect(() => {
    fetchDateOptions();
  }, [fetchDateOptions]);

  // Keep selectionRef in step with the live selection. Declared before the
  // fetch effects below so it is populated first on mount.
  useEffect(() => {
    selectionRef.current = {
      year: selectedYears[0] || null,
      month: selectedMonths[0] || null,
    };
  }, [selectedMonths, selectedYears]);

  useEffect(() => {
    const selectedYear = selectedYears[0];
    if (!selectedYear) return;

    const yearMonths = Array.isArray(monthsByYear[selectedYear])
      ? monthsByYear[selectedYear]
      : [];

    const selectedMonth = selectedMonths[0];
    if (selectedMonth && !yearMonths.includes(selectedMonth)) {
      setSelectedMonths(yearMonths.length > 0 ? [yearMonths[0]] : []);
    }
  }, [monthsByYear, selectedMonths, selectedYears]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  // Listen for timesheet updates via WebSocket
  useEffect(() => {
    if (!socket) return;

    const handleTimesheetUpdate = async () => {
      // Refresh both date options and timesheets when an update occurs
      await fetchDateOptions();
      await fetchTimesheets();
    };

    socket.on("timesheet:updated", handleTimesheetUpdate);
    return () => socket.off("timesheet:updated", handleTimesheetUpdate);
  }, [socket, fetchDateOptions, fetchTimesheets]);

  // Shared in-app refresh signal (2026-10-21).
  //
  // The WebSocket event above only covers changes made by OTHER sessions, and
  // it depends on the socket being connected. Saving a manual timeslot from the
  // overlay now bumps this signal too, so the list is guaranteed to refetch in
  // the same tab, whether or not the socket round-trip lands first.
  useEffect(() => {
    if (refreshPage === lastRefreshPageRef.current) return;
    lastRefreshPageRef.current = refreshPage;

    fetchDateOptions();
    fetchTimesheets();
  }, [refreshPage, fetchDateOptions, fetchTimesheets]);

  const selectorMonths = useMemo(() => {
    const monthSet = new Set();

    Object.values(monthsByYear).forEach((months) => {
      (Array.isArray(months) ? months : []).forEach((month) => {
        if (MONTH_NAMES.includes(month)) {
          monthSet.add(month);
        }
      });
    });

    return MONTH_NAMES.filter((month) => monthSet.has(month));
  }, [monthsByYear]);

  const handleDateSelectionSave = useCallback((selection) => {
    const nextYears = Array.isArray(selection?.years)
      ? selection.years.map((year) => String(year)).filter(Boolean)
      : [];

    const yearValue = nextYears[0] || "";
    const validMonthsForYear = yearValue
      ? Array.isArray(monthsByYear[yearValue])
        ? monthsByYear[yearValue]
        : []
      : [];

    const requestedMonth = Array.isArray(selection?.months)
      ? selection.months[0]
      : null;

    const nextMonth = requestedMonth && validMonthsForYear.includes(requestedMonth)
      ? requestedMonth
      : validMonthsForYear[0] || null;

    setSelectedYears(yearValue ? [yearValue] : []);
    setSelectedMonths(nextMonth ? [nextMonth] : []);
  }, [monthsByYear]);

  const handleEditTimeslot = useCallback((entry) => {
    setOverlayData({ timesheet: entry });
    setOverlayName(OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW);
  }, [setOverlayData, setOverlayName]);

  const rows = useMemo(
    () =>
      measurePerf("rows", () =>
        timesheets.map((entry, index) => {
          const liveEndTime = getSafeLiveEndTime(entry.ClockInAt, now);

          return {
            id: entry.id ?? `${entry.ClockInAt || "row"}-${index}`,
            rawEntry: entry,
            date: entry.ClockInAt || entry.ClockOutAt || null,
            clockInAt: entry.ClockInAt,
            clockOutAt: entry.ClockOutAt,
            liveClockOutAt: liveEndTime,
            duration: entry.ClockOutAt
              ? getDurationLabel(entry.ClockInAt, entry.ClockOutAt)
              : getLiveDurationLabel(entry.ClockInAt, liveEndTime),
            status: entry.ClockOutAt ? "Completed" : "In progress",
            notes: entry.Notes || "-",
          };
        }),
      ),
    [measurePerf, now, timesheets],
  );

  const numericHourlyRate = useMemo(() => {
    const parsed = Number(hourlyRate);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [hourlyRate]);

  const totalMinutesForSelectedPeriod = useMemo(() => {
    return measurePerf("totalMinutesForSelectedPeriod", () =>
      timesheets.reduce((sum, entry) => {
        const start = entry?.ClockInAt ? new Date(entry.ClockInAt) : null;
        const end = entry?.ClockOutAt ? new Date(entry.ClockOutAt) : now;

        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return sum;
        }

        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) {
          return sum;
        }

        return sum + (diffMs / 60000);
      }, 0),
    );
  }, [measurePerf, now, timesheets]);

  const monthlyTotalAmount = useMemo(() => {
    return measurePerf("monthlyTotalAmount", () =>
      timesheets.reduce((sum, entry) => {
        const start = entry?.ClockInAt ? new Date(entry.ClockInAt) : null;
        const end = entry?.ClockOutAt ? new Date(entry.ClockOutAt) : now;

        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return sum;
        }

        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) {
          return sum;
        }

        const minutes = diffMs / 60000;
        const rowRateParsed = Number(entry?.HourlyRateSnapshot);
        const effectiveRate = Number.isFinite(rowRateParsed) && rowRateParsed > 0
          ? rowRateParsed
          : numericHourlyRate;

        return sum + ((minutes / 60) * effectiveRate);
      }, 0),
    );
  }, [measurePerf, now, numericHourlyRate, timesheets]);

  const monthlyTotalLabel = useMemo(() => {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(monthlyTotalAmount);
  }, [monthlyTotalAmount]);

  const monthlyStats = useMemo(() => {
    return measurePerf("monthlyStats", () => {
      const rowDurationsInMinutes = rows
        .map((row) => {
          const start = row.clockInAt ? new Date(row.clockInAt) : null;
          const end = row.clockOutAt ? new Date(row.clockOutAt) : now;

          if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return null;
          }

          const diffMs = end.getTime() - start.getTime();
          if (diffMs <= 0) {
            return 0;
          }

          return diffMs / 60000;
        })
        .filter((minutes) => Number.isFinite(minutes));

      // Average per entry (was median — the median of per-entry durations
      // stayed flat at 1:25 because it only reflects the middle value, which
      // barely moves as new entries are added).
      const avgMinutesPerEntry =
        rowDurationsInMinutes.length > 0
          ? Math.round(
              rowDurationsInMinutes.reduce((sum, minutes) => sum + minutes, 0) /
                rowDurationsInMinutes.length,
            )
          : 0;

      const dayTotals = rows.reduce((acc, row) => {
        const dateValue = row.date ? new Date(row.date) : null;
        if (!dateValue || Number.isNaN(dateValue.getTime())) {
          return acc;
        }

        const dayKey = dateValue.toISOString().slice(0, 10);
        const start = row.clockInAt ? new Date(row.clockInAt) : null;
        const end = row.clockOutAt ? new Date(row.clockOutAt) : now;
        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return acc;
        }

        const diffMs = end.getTime() - start.getTime();
        const minutes = diffMs <= 0 ? 0 : diffMs / 60000;
        acc[dayKey] = (acc[dayKey] || 0) + minutes;
        return acc;
      }, {});

      const workedDays = Object.keys(dayTotals).length;
      const avgPerWorkedDayMinutes = workedDays > 0
        ? Math.round(totalMinutesForSelectedPeriod / workedDays)
        : 0;

      return {
        totalMinutes: totalMinutesForSelectedPeriod,
        avgMinutesPerEntry,
        avgMinutesPerWorkedDay: avgPerWorkedDayMinutes,
        workedDays,
        entryCount: rows.length,
      };
    });
  }, [measurePerf, now, rows, totalMinutesForSelectedPeriod]);

  const monthlyStatsLabels = useMemo(() => {
    const selectedYear = Number.parseInt(selectedYears?.[0] || "", 10);
    const selectedMonthIndex = MONTH_NAMES.indexOf(selectedMonths?.[0] || "");
    const monthWorkdays = getWorkdaysInMonth(selectedYear, selectedMonthIndex);
    const workedDaysLabel = Number.isInteger(monthWorkdays)
      ? `${monthlyStats.workedDays}/${monthWorkdays}`
      : String(monthlyStats.workedDays);

    return {
      totalHours: formatMinutesAsHHmm(monthlyStats.totalMinutes),
      averagePerEntry: formatMinutesAsHHmm(monthlyStats.avgMinutesPerEntry),
      averagePerWorkedDay: formatMinutesAsHHmm(monthlyStats.avgMinutesPerWorkedDay),
      workedDays: workedDaysLabel,
      entryCount: String(monthlyStats.entryCount),
    };
  }, [monthlyStats, selectedMonths, selectedYears]);

  const timesheetStatsCards = useMemo(() => {
    return [
      {
        key: "month-total",
        label: "Month Total",
        value: monthlyTotalLabel,
        icon: PaidRoundedIcon,
        color: theme.palette.primary.main,
        valueVariant: "h3",
      },
      {
        key: "total-hours",
        label: "Total Hours",
        value: monthlyStatsLabels.totalHours,
        icon: AccessTimeRoundedIcon,
        color: theme.palette.secondary.main,
        valueVariant: "h4",
      },
      {
        key: "average-entry",
        label: "Average",
        value: monthlyStatsLabels.averagePerEntry,
        icon: BarChartRoundedIcon,
        color: "#ff9800",
        valueVariant: "h4",
      },
      {
        key: "average",
        label: "Avg/Worked Day",
        value: monthlyStatsLabels.averagePerWorkedDay,
        icon: CalendarTodayRoundedIcon,
        color: "#f44336",
        valueVariant: "h4",
      },
      {
        key: "worked-days",
        label: "Worked Days",
        value: monthlyStatsLabels.workedDays,
        icon: LocalFireDepartmentRoundedIcon,
        color: theme.palette.primary.main,
        valueVariant: "h4",
      },
      {
        key: "entries",
        label: "Entries",
        value: monthlyStatsLabels.entryCount,
        icon: FormatListNumberedRoundedIcon,
        color: theme.palette.secondary.main,
        valueVariant: "h4",
      },
    ];
  }, [
    monthlyTotalLabel,
    monthlyStatsLabels.averagePerEntry,
    monthlyStatsLabels.averagePerWorkedDay,
    monthlyStatsLabels.entryCount,
    monthlyStatsLabels.totalHours,
    monthlyStatsLabels.workedDays,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ]);

  const groupedRows = useMemo(() => {
    return measurePerf("groupedRows", () => {
      const groups = rows.reduce((acc, row) => {
        const groupKey = row.date ? formatDateOnly(row.date) : "Undated";
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(row);
        return acc;
      }, {});

      return Object.entries(groups).map(([dateLabel, entries]) => ({
        dateLabel,
        entries: [...entries].sort((left, right) => {
          const leftTime = left.clockInAt ? new Date(left.clockInAt).getTime() : 0;
          const rightTime = right.clockInAt ? new Date(right.clockInAt).getTime() : 0;
          return rightTime - leftTime;
        }),
        totalDurationLabel: getTotalDurationLabel(entries),
      }));
    });
  }, [measurePerf, rows]);

  const dateRangeSelector = useMemo(() => ({
    label: "Date Range",
    months: selectorMonths,
    years: availableYears,
    selectedMonths,
    selectedYears,
    enabledYears: availableYears,
    enabledMonthsByYear: monthsByYear,
    onSave: handleDateSelectionSave,
    isMultiMonth: false,
    isMultiYear: false,
    monthGridItemSize: 3,
    yearGridItemSize: 4,
    showClearFilter: false,
    useInnerShadow: false,
  }), [
    selectorMonths,
    availableYears,
    selectedMonths,
    selectedYears,
    monthsByYear,
    handleDateSelectionSave,
  ]);

  const shouldRenderTopStatsAndFilter = inlineStatsAndFilter === true;
  const useActivityMatchedStyling = shouldRenderTopStatsAndFilter;
  const hourlyRateSubject = useMemo(() => {
    const trimmedName = String(memberName || "").trim();
    if (!trimmedName) {
      return "Your";
    }

    const endsWithS = /s$/i.test(trimmedName);
    return endsWithS ? `${trimmedName}'` : `${trimmedName}'s`;
  }, [memberName]);

  useEffect(() => {
    if (typeof onDateRangePropsChange === "function") {
      const signature = JSON.stringify({
        months: selectorMonths,
        years: availableYears,
        selectedMonths,
        selectedYears,
        enabledMonthsByYear: monthsByYear,
      });

      if (signature !== lastDateRangeSignatureRef.current) {
        lastDateRangeSignatureRef.current = signature;
        onDateRangePropsChange(dateRangeSelector);
      }
    }
  }, [
    onDateRangePropsChange,
    dateRangeSelector,
    selectorMonths,
    availableYears,
    selectedMonths,
    selectedYears,
    monthsByYear,
  ]);

  useImperativeHandle(ref, () => ({
    getDateRangeSelector: () => dateRangeSelector,
    refreshTimesheets: async () => {
      await fetchDateOptions();
      await fetchTimesheets();
    },
    getMonthlyTotal: () => ({
      amount: monthlyTotalAmount,
      label: monthlyTotalLabel,
      minutes: totalMinutesForSelectedPeriod,
    }),
  }), [
    dateRangeSelector,
    fetchDateOptions,
    fetchTimesheets,
    monthlyTotalAmount,
    monthlyTotalLabel,
    totalMinutesForSelectedPeriod,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        borderRadius={useOuterGridCard ? undefined : "12px"}
        bgcolor={useOuterGridCard ? undefined : theme.palette.background.default}
        sx={{
          boxShadow: useOuterGridCard ? "none" : theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              //backgroundColor: theme.palette.background.alt,
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            {numericHourlyRate <= 0 && (
              <Typography
                variant="body2"
                fontWeight={700}
                color={theme.palette.warning.main}
                sx={{ textAlign: "center" }}
              >
                {hourlyRateSubject} hourly rate is currently $0.00. Please update the rate in Settings to ensure totals are calculated correctly.
              </Typography>
            )}
            <Box
              sx={{
                width: "100%",
                display: { xs: "flex", sm: "grid" },
                gridTemplateColumns: {
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(6, minmax(0, 1fr))",
                },
                gap: 2,
                overflowX: { xs: "auto", sm: "visible" },
                pb: { xs: 0.5, sm: 0 },
              }}
            >
              {timesheetStatsCards.map((statCard) => {
                const StatIcon = statCard.icon;
                return (
                  <Card
                    key={statCard.key}
                    elevation={2}
                    sx={{
                      borderRadius: "12px",
                      height: "100%",
                      flex: { xs: "0 0 auto", sm: "unset" },
                      width: { xs: 150, sm: "auto" },
                    }}
                  >
                    <CardContent
                      sx={{
                        textAlign: "center",
                        minHeight: { xs: 104, sm: 156, md: 92 },
                        py: { xs: 1, sm: 2, md: 1 },
                        "&:last-child": { paddingBottom: { xs: 1, sm: 3, md: 1 } },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <StatIcon
                        sx={{
                          fontSize: { xs: 26, sm: 40, md: 28 },
                          color: statCard.color,
                          mb: { xs: 0.5, sm: 1, md: 0.5 },
                        }}
                      />
                      {statCard.key === "month-total" ? (
                        <HourlyRateHoverCell
                          value={statCard.value}
                          variant={isMobile || isCompactStats ? "h6" : statCard.valueVariant}
                        />
                      ) : (
                        <Typography
                          variant={isMobile || isCompactStats ? "h6" : statCard.valueVariant}
                          fontWeight="bold"
                        >
                          {statCard.value}
                        </Typography>
                      )}
                      <Typography variant={isMobile || isCompactStats ? "caption" : "body2"} color="text.secondary">
                        {statCard.label}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <CustomDateSelector {...dateRangeSelector} useSimpleStyle />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: useActivityMatchedStyling ? 0 : "12px",
            flex: 1,
            minHeight: 0,
            // This box owns the table's ONE horizontal scroller: the header
            // row and the day cards share it, so they pan left/right as one
            // unit. The vertical scroll lives one level down inside the list
            // wrapper — that is why nothing can travel behind the header.
            // (overflow-y computes to "auto" alongside overflow-x, but the
            // flex column below is height-bounded and never overflows.)
            display: "flex",
            flexDirection: "column",
            overflowX: "auto",
            px: { xs: 1, sm: 1.5 },
            py: 1,
          }}
        >
          {loadingTimesheets ? (
            <Box
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading timesheet entries...
              </Typography>
            </Box>
          ) : groupedRows.length === 0 ? (
            <Box
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No timesheet entries found for the selected period.
              </Typography>
            </Box>
          ) : (
            <Profiler id="TimesheetsRows" onRender={onTimesheetsProfilerRender}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                flex: 1,
                minHeight: 0,
                // Same minimum as the header row and the day cards below, so
                // the wrapper IS the table's horizontal scroll width: on a
                // narrow desktop the inner scroller is forced at least this
                // wide, its own computed overflow-x never activates, and the
                // single outer scroller pans header + cards together.
                minWidth: { xs: 0, md: TIMESHEET_TABLE_MIN_WIDTH },
              }}
            >
              {!isMobile && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `${TIMESHEET_DAY_COLUMN_WIDTH} minmax(0, 1fr)`,
                    minWidth: { xs: 0, md: TIMESHEET_TABLE_MIN_WIDTH },
                    px: 1.5,
                    py: 1,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: useActivityMatchedStyling ? "10px" : "12px",
                    // Layout-pinned, not sticky: this row sits ABOVE the
                    // cards' vertical scroller, so the list clips at the
                    // scroller's top edge instead of sliding behind this bar.
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>
                    Day
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: TIMESHEET_DESKTOP_COLUMNS,
                      gap: 1,
                      pl: 1.5,
                      borderLeft: `1px solid ${theme.palette.primary.border2}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Clock In</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Clock Out</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Duration</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Status</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Notes</Typography>
                  </Box>
                </Box>
              )}

              {/* The cards' own vertical scroller: it fills the space left
                  under the header and clips the list there, so no card can
                  ever scroll behind the header row. The py is cancelled by
                  the matching negative margin — cards keep the exact header
                  alignment and the 12px gap, while the padding box gives the
                  clip line room above and below. No left padding/margin
                  pair here: it widened this box 12px past its slot, and an
                  overflow-y of "auto" computes overflow-x to "auto" too —
                  combined with the cards' 1026px minimum vs. the ~17px the
                  vertical scrollbar takes off the content box, this box
                  genuinely overflows horizontally and grew a SECOND
                  horizontal scrollbar of its own. overflowX is pinned to
                  "hidden" so it can never be a horizontal scroll container;
                  the one horizontal scroller stays on the outer Box above,
                  and its scroll extent ends on the cards' right border
                  (1026), flush with the header row. Trade-off: the vertical
                  scrollbar overlays the cards' last ~17px, i.e. the right
                  edge of the notes column. */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  py: 1,
                  my: -1,
                }}
              >
              {groupedRows.map((group) => (
                <Box
                  key={group.dateLabel}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: `${TIMESHEET_DAY_COLUMN_WIDTH} minmax(0, 1fr)`,
                    },
                    // Same minimum as the header row above, so the two end at
                    // the same right edge inside the horizontal scroller
                    // (xs stays 0 — the mobile card is a single-column stack).
                    minWidth: { xs: 0, md: TIMESHEET_TABLE_MIN_WIDTH },
                    // The scroller above is a height-bounded flex column, so
                    // each card is a flex item with the default flex-shrink:
                    // 1 — and this card's overflow:"hidden" zeroes its
                    // automatic minimum size, letting it squash without limit.
                    // Left alone, the cards would compress to fit the scroller
                    // instead of overflowing it: no overflow means no
                    // scrollbar and no scrolling at all. Pinning the shrink
                    // to 0 keeps every card at its natural height so the list
                    // overflows the scroller and actually scrolls.
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: useActivityMatchedStyling ? "12px" : "16px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: useActivityMatchedStyling ? "none" : theme.shadows[2],
                    transition: useActivityMatchedStyling
                      ? "background-color 0.2s ease"
                      : "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: useActivityMatchedStyling ? "none" : theme.shadows[4],
                      transform: useActivityMatchedStyling ? "none" : "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.background.alt,
                      borderRight: { xs: "none", md: `1px solid ${theme.palette.divider}` },
                      borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color={theme.palette.primary.main}>
                      {group.dateLabel}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Total: {group.totalDurationLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {group.entries.map((entry, entryIndex) => {
                      const isInProgress = entry.status === "In progress";
                      const hasNotes = Boolean(entry.notes && entry.notes !== "-");

                      return (
                        <Box
                          key={entry.id}
                          role={allowEdit ? "button" : undefined}
                          tabIndex={allowEdit ? 0 : -1}
                          aria-label={`Edit timeslot ${formatTimeOnly(entry.clockInAt)} to ${formatTimeOnly(entry.clockOutAt)}`}
                          onClick={allowEdit ? () => handleEditTimeslot(entry.rawEntry) : undefined}
                          onKeyDown={(event) => {
                            if (!allowEdit) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleEditTimeslot(entry.rawEntry);
                            }
                          }}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "repeat(2, minmax(0, 1fr))",
                              md: TIMESHEET_DESKTOP_COLUMNS,
                            },
                            gap: { xs: 1, md: 1 },
                            px: 1.5,
                            py: 1.25,
                            cursor: allowEdit ? "pointer" : "default",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom:
                              entryIndex === group.entries.length - 1
                                ? "none"
                                : `1px solid ${theme.palette.primary.border2}`,
                            alignItems: "center",
                            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              backgroundColor: allowEdit
                                ? theme.palette.action.hover
                                : theme.palette.background.paper,
                            },
                            "&:focus-visible": {
                              outline: allowEdit
                                ? `2px solid ${theme.palette.primary.main}`
                                : "none",
                              outlineOffset: -2,
                            },
                          }}
                        >
                          <Box>
                            {isMobile && (
                              // display:block is required — a caption span is
                              // inline, so it would sit flush against the value
                              // ("StatusCompleted") and `mb` would not apply.
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25 }}
                              >
                                Clock In
                              </Typography>
                            )}
                            <Typography variant="body2" fontWeight={600}>
                              {formatTimeOnly(entry.clockInAt)}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25 }}
                              >
                                Clock Out
                              </Typography>
                            )}
                            <Typography variant="body2" fontWeight={entry.clockOutAt ? 600 : 400}>
                              {entry.clockOutAt
                                ? formatTimeOnly(entry.clockOutAt)
                                : formatTimeOnly(entry.liveClockOutAt || now)}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25 }}
                              >
                                Duration
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              color={entry.clockOutAt ? theme.palette.primary.main : theme.palette.info.main}
                              fontWeight={entry.clockOutAt ? 600 : 400}
                            >
                              {entry.duration}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25 }}
                              >
                                Status
                              </Typography>
                            )}
                            <Chip
                              label={entry.status}
                              size="small"
                              variant="filled"
                              sx={{
                                borderRadius: "8px",
                                fontWeight: 500,
                                backgroundColor: isInProgress
                                  ? theme.palette.info.main
                                  : theme.palette.primary.main,
                                color: isInProgress
                                  ? theme.palette.info.contrastText
                                  : theme.palette.common.white,
                                "& .MuiChip-label": {
                                  px: 1,
                                  color: isInProgress
                                    ? theme.palette.info.contrastText
                                    : theme.palette.common.white,
                                },
                              }}
                            />
                          </Box>

                          <TimesheetNotesCell
                            isMobile={isMobile}
                            notes={entry.notes}
                            hasNotes={hasNotes}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
            </Profiler>
          )}
        </Box>

        {!shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.alt,
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              gap: detailsPanelExpanded ? 1 : 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              {!detailsPanelExpanded && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    minWidth: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    Month total time: {monthlyStatsLabels.totalHours}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}
                  >
                    Month total pay: {" "}
                    <HourlyRateHoverCell value={monthlyTotalLabel} variant="body2" />
                  </Typography>
                </Box>
              )}

              <Box
                component="button"
                type="button"
                onClick={() => setDetailsPanelExpanded((prev) => !prev)}
                aria-label={detailsPanelExpanded ? "Collapse details" : "Expand details"}
                title={detailsPanelExpanded ? "Collapse details" : "Expand details"}
                sx={{
                  border: "none",
                  background: "none",
                  p: 0,
                  m: 0,
                  ml: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  lineHeight: 0,
                }}
              >
                {detailsPanelExpanded ? (
                  <ExpandMoreRoundedIcon fontSize="small" />
                ) : (
                  <ExpandLessRoundedIcon fontSize="small" />
                )}
              </Box>
            </Box>

            {detailsPanelExpanded ? (
              <>
                {numericHourlyRate <= 0 && (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={theme.palette.warning.main}
                    sx={{ textAlign: "center" }}
                  >
                    {hourlyRateSubject} hourly rate is currently $0.00. Please update the rate in Settings to ensure totals are calculated correctly.
                  </Typography>
                )}
                <Box
                  sx={{
                    width: "100%",
                    display: { xs: "flex", sm: "grid" },
                    gridTemplateColumns: {
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: "repeat(6, minmax(0, 1fr))",
                    },
                    gap: 2,
                    overflowX: { xs: "auto", sm: "visible" },
                    pb: { xs: 0.5, sm: 0 },
                  }}
                >
                  {timesheetStatsCards.map((statCard) => {
                    const StatIcon = statCard.icon;
                    return (
                      <Card
                        key={statCard.key}
                        elevation={2}
                        sx={{
                          borderRadius: "12px",
                          height: "100%",
                          flex: { xs: "0 0 auto", sm: "unset" },
                          width: { xs: 150, sm: "auto" },
                        }}
                      >
                        <CardContent
                          sx={{
                            textAlign: "center",
                            minHeight: { xs: 104, sm: 156, md: 92 },
                            py: { xs: 1, sm: 2, md: 1 },
                            "&:last-child": { paddingBottom: { xs: 1, sm: 3, md: 1 } },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <StatIcon
                            sx={{
                              fontSize: { xs: 26, sm: 40, md: 28 },
                              color: statCard.color,
                              mb: { xs: 0.5, sm: 1, md: 0.5 },
                            }}
                          />
                          {statCard.key === "month-total" ? (
                            <HourlyRateHoverCell
                              value={statCard.value}
                              variant={isMobile || isCompactStats ? "h6" : statCard.valueVariant}
                            />
                          ) : (
                            <Typography
                              variant={isMobile || isCompactStats ? "h6" : statCard.valueVariant}
                              fontWeight="bold"
                            >
                              {statCard.value}
                            </Typography>
                          )}
                          <Typography variant={isMobile || isCompactStats ? "caption" : "body2"} color="text.secondary">
                            {statCard.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
});

TimesheetsTab.displayName = "TimesheetsTab";

export default TimesheetsTab;
